/**
 * Shared WBS / CBS / category + availability hint for spend line editors.
 * When an Approved Current/Original Budget exists, options are limited to
 * budget lines for the selected project (not the full project WBS tree).
 */
import React from 'react';
import { Alert, Select, Space } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { BudgetAvailabilityHint } from './BudgetAvailabilityHint';
import { BudgetCodeSelect } from './BudgetCodeSelect';
import { projectService } from '@/services/project.service';
import type { Est3WbsNode } from '@/utils/est3WbsTree';

const COST_CATEGORIES = ['Material', 'Labor', 'Subcontract', 'Overhead', 'Equipment'];

export type CostControlLineFieldsValue = {
  wbs_node_id?: string | null;
  cbs_code_id?: string | null;
  cost_category?: string | null;
};

export type CostControlLineFieldsProps = {
  projectId?: string | null;
  value: CostControlLineFieldsValue;
  onChange: (patch: CostControlLineFieldsValue) => void;
  disabled?: boolean;
  size?: 'small' | 'middle';
  /** Line amount — used to mark Over vs Available */
  requestedAmount?: number | null;
};

function mapStructureNodes(
  raw: Record<string, unknown>[],
  nameKeys: string[]
): Est3WbsNode[] {
  return raw.map((n) => {
    let name = '';
    for (const k of nameKeys) {
      if (n[k]) {
        name = String(n[k]);
        break;
      }
    }
    return {
      id: String(n.id),
      code: String(n.code || ''),
      name: name || String(n.code || ''),
      parent_id: (n.parent_id as string) || null,
      sort_order: Number(n.sort_order || 0),
      is_active: n.is_active as number | boolean,
    };
  });
}

function ensureNode(
  nodes: Est3WbsNode[],
  id: string | null | undefined,
  fallbackLabel?: string
): Est3WbsNode[] {
  if (!id) return nodes;
  if (nodes.some((n) => n.id === id)) return nodes;
  return [
    ...nodes,
    {
      id,
      code: fallbackLabel || id.slice(0, 8),
      name: fallbackLabel || 'Budget line',
      parent_id: null,
      sort_order: 0,
      is_active: true,
    },
  ];
}

export const CostControlLineFields: React.FC<CostControlLineFieldsProps> = ({
  projectId,
  value,
  onChange,
  disabled,
  size = 'small',
  requestedAmount,
}) => {
  const structureQ = useQuery({
    queryKey: ['project-budget-structure', projectId],
    queryFn: () => projectService.getBudgetStructureContext(String(projectId)),
    enabled: !!projectId,
    staleTime: 60_000,
  });

  const baselineQ = useQuery({
    queryKey: ['project-budget-baseline', projectId],
    queryFn: () => projectService.getBudgetBaseline(String(projectId)),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const budgetLines = React.useMemo(
    () => ((baselineQ.data?.lines || []) as Record<string, unknown>[]),
    [baselineQ.data]
  );
  const controlActive = !!baselineQ.data?.revision && budgetLines.length > 0;

  const allWbs = React.useMemo(() => {
    const nodes = ((structureQ.data as any)?.nodes || []) as Record<string, unknown>[];
    return mapStructureNodes(nodes, ['title', 'name']);
  }, [structureQ.data]);

  const allCbs = React.useMemo(() => {
    const nodes = ((structureQ.data as any)?.cbs_codes || []) as Record<string, unknown>[];
    return mapStructureNodes(nodes, ['name', 'title']);
  }, [structureQ.data]);

  const linesForWbs = React.useMemo(() => {
    if (!value.wbs_node_id) return budgetLines;
    return budgetLines.filter((l) => String(l.wbs_node_id || '') === String(value.wbs_node_id));
  }, [budgetLines, value.wbs_node_id]);

  const wbsNodes: Est3WbsNode[] = React.useMemo(() => {
    if (!controlActive) return allWbs;
    const ids = new Set(
      budgetLines.map((l) => (l.wbs_node_id ? String(l.wbs_node_id) : '')).filter(Boolean)
    );
    const filtered = allWbs.filter((n) => ids.has(n.id));
    return ensureNode(filtered, value.wbs_node_id);
  }, [controlActive, allWbs, budgetLines, value.wbs_node_id]);

  const cbsNodes: Est3WbsNode[] = React.useMemo(() => {
    if (!controlActive) return allCbs;
    const source = value.wbs_node_id ? linesForWbs : budgetLines;
    const ids = new Set(
      source.map((l) => (l.cbs_code_id ? String(l.cbs_code_id) : '')).filter(Boolean)
    );
    const filtered = allCbs.filter((n) => ids.has(n.id));
    return ensureNode(filtered, value.cbs_code_id);
  }, [controlActive, allCbs, budgetLines, linesForWbs, value.wbs_node_id, value.cbs_code_id]);

  const categoryOptions = React.useMemo(() => {
    if (!controlActive) return COST_CATEGORIES;
    const source = value.wbs_node_id ? linesForWbs : budgetLines;
    const cats = [
      ...new Set(
        source
          .map((l) => String(l.cost_category || '').trim())
          .filter(Boolean)
      ),
    ];
    if (value.cost_category && !cats.includes(value.cost_category)) {
      cats.push(value.cost_category);
    }
    return cats.length ? cats : COST_CATEGORIES;
  }, [controlActive, budgetLines, linesForWbs, value.wbs_node_id, value.cost_category]);

  const pickCategoryForWbs = (wbsId: string | null): string => {
    if (!wbsId || !controlActive) return value.cost_category || 'Material';
    const forWbs = budgetLines.filter((l) => String(l.wbs_node_id || '') === String(wbsId));
    const cats = forWbs.map((l) => String(l.cost_category || '').trim()).filter(Boolean);
    if (value.cost_category && cats.includes(value.cost_category)) return value.cost_category;
    return cats[0] || value.cost_category || 'Material';
  };

  const cbsValidForWbs = (wbsId: string | null, cbsId: string | null): boolean => {
    if (!cbsId || !controlActive) return true;
    const source = wbsId
      ? budgetLines.filter((l) => String(l.wbs_node_id || '') === String(wbsId))
      : budgetLines;
    return source.some((l) => String(l.cbs_code_id || '') === String(cbsId));
  };

  if (!projectId) return null;

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {controlActive && wbsNodes.length === 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ padding: '4px 8px' }}
          message="No WBS on approved budget"
          description="Add WBS lines to the Current/Original Budget, then select here."
        />
      )}
      <BudgetCodeSelect
        size={size}
        kind="wbs"
        nodes={wbsNodes}
        value={value.wbs_node_id || undefined}
        disabled={disabled}
        placeholder={controlActive ? 'Budget WBS' : 'WBS'}
        notFoundContent={
          controlActive ? 'No WBS on approved budget for this project' : undefined
        }
        onChange={(v) => {
          const wbs = (v as string) || null;
          const nextCat = pickCategoryForWbs(wbs);
          const keepCbs = cbsValidForWbs(wbs, value.cbs_code_id || null)
            ? value.cbs_code_id
            : null;
          onChange({
            ...value,
            wbs_node_id: wbs,
            cbs_code_id: keepCbs ?? null,
            cost_category: nextCat,
          });
        }}
      />
      <BudgetCodeSelect
        size={size}
        kind="cbs"
        nodes={cbsNodes}
        value={value.cbs_code_id || undefined}
        disabled={disabled}
        placeholder={controlActive ? 'Budget CBS (optional)' : 'CBS (optional)'}
        notFoundContent={
          controlActive ? 'No CBS on approved budget for this WBS' : undefined
        }
        onChange={(v) => onChange({ ...value, cbs_code_id: (v as string) || null })}
      />
      <Select
        size={size}
        style={{ width: '100%' }}
        value={value.cost_category || categoryOptions[0] || 'Material'}
        disabled={disabled}
        options={categoryOptions.map((c) => ({ value: c, label: c }))}
        onChange={(v) => onChange({ ...value, cost_category: v })}
      />
      <BudgetAvailabilityHint
        projectId={projectId}
        wbsNodeId={value.wbs_node_id}
        cbsCodeId={value.cbs_code_id}
        costCategory={value.cost_category || 'Material'}
        requestedAmount={requestedAmount}
      />
    </Space>
  );
};

export default CostControlLineFields;
